/**
 * Rule results the page host embeds in the page, read by the DSL runtime
 * under the reserved `context.decisions` key. The host evaluates decisions on
 * the server and may update the entry after load; the runtime only looks
 * answers up and never evaluates a rule.
 */

/** The `flowContext` key the page host fills with rule results. */
export const DECISIONS_CONTEXT_KEY = 'decisions';

/** The `flowContext` key naming the platform variant this render is for. */
export const PLATFORM_VARIANT_CONTEXT_KEY = 'platformVariant';

/**
 * `flowContext` keys only the page host may fill. Authored config cannot
 * write them through a step handle, a control's `formKey` or `_meta` handle,
 * or a `formState.set` action, so a config can never fake a rule result.
 */
export const HOST_ONLY_CONTEXT_KEYS: ReadonlySet<string> = new Set([
  DECISIONS_CONTEXT_KEY,
  PLATFORM_VARIANT_CONTEXT_KEY,
]);

export type DecisionStatus =
  | 'matched'
  | 'unmatched'
  | 'invalid-input'
  | 'unavailable-decision'
  | 'error';

/**
 * An entry for one decision: a single value, or, for a variant-scoped
 * decision, one value per platform variant.
 */
export type PerVariant<T> = T | Readonly<Record<string, T>>;

export type EmbeddedDecisions = {
  /** Decision id to the outcome id it produced. */
  readonly outcomes?: Readonly<Record<string, PerVariant<string>>>;
  /** Decision id to its status. */
  readonly statuses?: Readonly<Record<string, PerVariant<DecisionStatus>>>;
  /**
   * Definition id to its computed value, as the service returns it. The
   * runtime shows each as display text under `values` and keeps the typed
   * value under `valueDetails`.
   */
  readonly values?: Readonly<Record<string, unknown>>;
  /** The decision set version that produced these results. */
  readonly decisionSet?: {
    readonly decisionSetId: string;
    readonly version: number;
  };
};

/** One decision's answer for this render. `missing` when the page has none. */
export type DecisionAnswer =
  | { readonly status: 'matched'; readonly outcomeId: string }
  | { readonly status: Exclude<DecisionStatus, 'matched'> | 'missing' };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const ownEntry = (record: unknown, key: string): unknown =>
  isRecord(record) && Object.prototype.hasOwnProperty.call(record, key)
    ? record[key]
    : undefined;

/** Picks the current variant's value from a variant-scoped entry. */
function forVariant(entry: unknown, platformVariant: string | undefined) {
  if (!isRecord(entry)) return entry;
  return platformVariant ? ownEntry(entry, platformVariant) : undefined;
}

const STATUSES: ReadonlySet<string> = new Set([
  'matched',
  'unmatched',
  'invalid-input',
  'unavailable-decision',
  'error',
]);

/**
 * Looks up one decision's answer. A key lookup only: an outcome without a
 * status counts as matched, a status other than `matched` wins over any
 * outcome, and a variant-scoped entry with no current variant, a malformed
 * entry, or no entry at all is `missing`, so the caller renders its fallback.
 */
export function readDecisionAnswer(
  decisions: unknown,
  decisionId: string,
  platformVariant?: string,
): DecisionAnswer {
  const id = decisionId.trim();
  if (!id) return { status: 'missing' };
  const outcome = forVariant(
    ownEntry(ownEntry(decisions, 'outcomes'), id),
    platformVariant,
  );
  const rawStatus = forVariant(
    ownEntry(ownEntry(decisions, 'statuses'), id),
    platformVariant,
  );
  if (
    rawStatus !== undefined &&
    (typeof rawStatus !== 'string' || !STATUSES.has(rawStatus))
  ) {
    return { status: 'missing' };
  }
  const status =
    (rawStatus as DecisionStatus | undefined) ??
    (outcome === undefined ? undefined : 'matched');
  if (status === undefined) return { status: 'missing' };
  if (status !== 'matched') return { status };
  return typeof outcome === 'string' && outcome.trim()
    ? { status: 'matched', outcomeId: outcome }
    : { status: 'missing' };
}
